import React, { useState, useEffect } from 'react';
import './YearView.css';
import { loadMonthData, getDaysInMonth, getFirstDayOfMonth } from '../utils/storage';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const DAYS_OF_WEEK = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

const YearView = ({ year, onMonthClick }) => {
  const [monthsData, setMonthsData] = useState({});

  useEffect(() => {
    const loadAllMonths = () => {
      const data = {};
      for (let i = 0; i < 12; i++) {
        data[i] = loadMonthData(i, year);
      }
      setMonthsData(data);
    };

    loadAllMonths();
    // Recargar cada 2 segundos para detectar cambios
    const interval = setInterval(loadAllMonths, 2000);
    return () => clearInterval(interval);
  }, [year]);

  const getEmojiFlagRegex = () => /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;

  const getDayEvents = (monthIndex, day) => {
    const monthData = monthsData[monthIndex];
    if (!monthData?.plan?.calendario) return [];

    const dayKey = day.toString();
    const dayText = monthData.plan.calendario[dayKey] || '';
    if (!dayText.trim()) return [];

    const lines = dayText.split('\n').filter(line => line.trim());
    const events = [];

    lines.forEach(line => {
      const trimmed = line.trim();
      // Detectar tipo de evento
      if (trimmed.match(getEmojiFlagRegex()) || trimmed.startsWith('📍')) {
        events.push({ type: 'viaje', text: trimmed });
      } else if (trimmed.startsWith('✈️')) {
        events.push({ type: 'vuelo', text: trimmed });
      } else if (trimmed.startsWith('🚂')) {
        events.push({ type: 'tren', text: trimmed });
      } else if (trimmed.startsWith('🚌')) {
        events.push({ type: 'bus', text: trimmed });
      } else if (trimmed.startsWith('🏠')) {
        events.push({ type: 'alojamiento', text: trimmed });
      } else if (trimmed.trim()) {
        events.push({ type: 'manual', text: trimmed });
      }
    });

    return events;
  };

  const renderMiniCalendar = (monthIndex) => {
    const daysInMonth = getDaysInMonth(monthIndex, year);
    const firstDay = getFirstDayOfMonth(monthIndex, year);
    const cells = [];

    // Días vacíos al inicio
    for (let i = 0; i < firstDay; i++) {
      cells.push(
        <div key={`empty-${i}`} className="year-calendar-cell empty" />
      );
    }

    // Días del mes
    for (let day = 1; day <= daysInMonth; day++) {
      const events = getDayEvents(monthIndex, day);
      const hasEvents = events.length > 0;

      cells.push(
        <div
          key={`day-${day}`}
          className={`year-calendar-cell ${hasEvents ? 'has-events' : ''}`}
          title={hasEvents ? events.map(e => e.text).join('\n') : ''}
        >
          <div className="year-day-number">{day}</div>
          {hasEvents && (
            <div className="year-events-dots">
              {events.slice(0, 3).map((event, idx) => {
                let dotClass = 'year-event-dot';
                if (event.type === 'viaje') dotClass += ' dot-viaje';
                else if (event.type === 'vuelo') dotClass += ' dot-vuelo';
                else if (event.type === 'tren') dotClass += ' dot-tren';
                else if (event.type === 'bus') dotClass += ' dot-bus';
                else if (event.type === 'alojamiento') dotClass += ' dot-alojamiento';
                else dotClass += ' dot-manual';

                return <span key={idx} className={dotClass} />;
              })}
              {events.length > 3 && (
                <span className="year-event-more">+{events.length - 3}</span>
              )}
            </div>
          )}
        </div>
      );
    }

    return cells;
  };

  const getMonthStats = (monthIndex) => {
    const monthData = monthsData[monthIndex];
    if (!monthData?.plan) {
      return { viajes: 0, eventos: 0, objetivo: false };
    }

    const viajes = monthData.plan.viajes?.length || 0;
    let eventos = 0;
    const calendario = monthData.plan.calendario || {};
    
    Object.keys(calendario).forEach(dayKey => {
      const dayText = calendario[dayKey] || '';
      if (dayText.trim()) {
        eventos += dayText.split('\n').filter(line => line.trim()).length;
      }
    });

    const objetivo = monthData.plan.objetivoViajero?.trim() || false;

    return { viajes, eventos, objetivo };
  };

  return (
    <div className="year-view-container">
      <header className="year-view-header">
        <h1 className="year-view-title">Vista Anual {year}</h1>
        <p className="year-view-subtitle">Haz clic en un mes para ver detalles</p>
      </header>

      <div className="year-months-grid">
        {MONTHS.map((monthName, monthIndex) => {
          const stats = getMonthStats(monthIndex);
          const hasData = stats.viajes > 0 || stats.eventos > 0 || stats.objetivo;

          return (
            <div
              key={monthIndex}
              className={`year-month-card ${hasData ? 'has-data' : ''}`}
              onClick={() => onMonthClick(monthIndex)}
            >
              <div className="year-month-header">
                <h3 className="year-month-name">{monthName}</h3>
                {hasData && (
                  <div className="year-month-badges">
                    {stats.viajes > 0 && (
                      <span className="year-month-badge badge-viajes">
                        {stats.viajes} {stats.viajes === 1 ? 'viaje' : 'viajes'}
                      </span>
                    )}
                    {stats.eventos > 0 && (
                      <span className="year-month-badge badge-eventos">
                        {stats.eventos} eventos
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="year-calendar-wrapper">
                <div className="year-calendar-header">
                  {DAYS_OF_WEEK.map((day, idx) => (
                    <div key={idx} className="year-calendar-header-cell">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="year-calendar-grid">
                  {renderMiniCalendar(monthIndex)}
                </div>
              </div>

              {stats.objetivo && (
                <div className="year-month-objective">
                  <span className="objective-icon">🎯</span>
                  <span className="objective-text" title={stats.objetivo}>
                    {stats.objetivo.length > 50 
                      ? stats.objetivo.substring(0, 50) + '...' 
                      : stats.objetivo}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default YearView;

